import { useEffect } from "react";
import { BACKEND_URL } from "../../lib/api";

const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "appfillip_active_lead_draft";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Career pages are job applications, not sales leads (handled by /api/careers/apply).
const isCareerPage = () => /^\/careers?(\/|$)/i.test(window.location.pathname);

function isLeadForm(form) {
  if (isCareerPage()) return false;
  if (form.matches("[data-no-lead-capture], [data-no-lead-capture] *")) return false;
  if (form.querySelector('input[type="file"], input[type="password"]')) return false;
  const email = form.querySelector('input[type="email"], input[name*="email" i]');
  const phone = form.querySelector('input[type="tel"], input[name*="phone" i]');
  return Boolean(email || phone);
}

function fieldValue(form, pattern) {
  const field = Array.from(form.elements).find((element) => {
    const key = `${element.name || ""} ${element.id || ""} ${element.placeholder || ""}`
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/[_-]+/g, " ");
    return pattern.test(key) && element.value?.trim();
  });
  return field?.value?.trim() || "";
}

function identifyFields(form) {
  const emailInput = form.querySelector('input[type="email"], input[name*="email" i]');
  const phoneInput = form.querySelector('input[type="tel"], input[name*="phone" i]');
  const email = emailInput?.value?.trim().toLowerCase() || "";
  const phone = phoneInput?.value?.trim() || "";
  if ((!email || !EMAIL_RE.test(email)) && phone.replace(/\D/g, "").length < 8) return null;

  if (!form.dataset.leadDraftId) {
    form.dataset.leadDraftId = window.crypto?.randomUUID?.() || `draft-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
  return {
    draft_id: form.dataset.leadDraftId,
    name: fieldValue(form, /(^|\s)(name|full name|your name)(\s|$)/i),
    email: EMAIL_RE.test(email) ? email : "",
    phone,
    app_name: fieldValue(form, /(^|\s)(app name|application name|company name|your app)(\s|$)/i),
    source: `form:${window.location.pathname}`,
    page_url: `${window.location.origin}${window.location.pathname}`,
  };
}

function addNotice(form) {
  if (form.dataset.leadCaptureNotice || !isLeadForm(form)) return;
  const notice = document.createElement("p");
  notice.dataset.leadCaptureNotice = "true";
  notice.className = "mb-3 text-xs leading-relaxed text-zinc-500";
  notice.textContent = "If you enter your email or phone, we save it so we can follow up if you leave before submitting.";
  form.prepend(notice);
  form.dataset.leadCaptureNotice = "true";
}

export default function LeadDraftCapture() {
  useEffect(() => {
    const timers = new WeakMap();
    const scanForms = () => document.querySelectorAll("form").forEach(addNotice);
    scanForms();

    const observer = new MutationObserver(scanForms);
    observer.observe(document.body, { childList: true, subtree: true });

    const saveDraft = (form) => {
      if (!isLeadForm(form)) return;
      const pending = timers.get(form);
      if (pending) clearTimeout(pending);
      const draft = identifyFields(form);
      if (!draft) return;
      const fingerprint = JSON.stringify(draft);
      if (form.dataset.leadDraftFingerprint === fingerprint) return;
      form.dataset.leadDraftFingerprint = fingerprint;
      try {
        window.localStorage.setItem(STORAGE_KEY, draft.draft_id);
      } catch { /* Storage may be disabled; the draft still saves server-side. */ }

      fetch(`${API}/leads/draft`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        body: JSON.stringify(draft),
      }).catch(() => {
        if (form.dataset.leadDraftFingerprint === fingerprint) {
          delete form.dataset.leadDraftFingerprint;
        }
      });
    };

    const onInput = (event) => {
      const form = event.target?.form;
      if (!form || !isLeadForm(form)) return;
      const oldTimer = timers.get(form);
      if (oldTimer) clearTimeout(oldTimer);
      timers.set(form, setTimeout(() => saveDraft(form), 700));
    };
    const onFocusOut = (event) => {
      const form = event.target?.form;
      if (form) saveDraft(form);
    };
    const onPageHide = () => document.querySelectorAll("form").forEach(saveDraft);

    document.addEventListener("input", onInput, true);
    document.addEventListener("change", onInput, true);
    document.addEventListener("focusout", onFocusOut, true);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      observer.disconnect();
      document.removeEventListener("input", onInput, true);
      document.removeEventListener("change", onInput, true);
      document.removeEventListener("focusout", onFocusOut, true);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, []);

  return null;
}
